import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";

interface Results {
  accounts: { id: string; name: string; number: string; stage: string }[];
  contacts: { id: string; name: string; email: string | null; accountId: string; accountName: string }[];
  tickets: { id: string; number: number; title: string; state: string; accountId: string; accountName: string }[];
  emails: { id: string; threadId: string; subject: string; fromAddress: string; snippet: string | null }[];
}

export function CommandK({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState<Results | null>(null);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) { setQ(""); setRes(null); setActive(0); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get<Results>(`/search?q=${encodeURIComponent(q.trim())}`).then(setRes).catch(() => setRes(null)), 120);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;
  const items: { key: string; group: string; label: string; sub?: string; to: string }[] = [];
  res?.accounts.forEach((a) => items.push({ key: "a" + a.id, group: "Accounts", label: a.name, sub: a.number, to: `/accounts/${a.id}` }));
  res?.contacts.forEach((c) => items.push({ key: "c" + c.id, group: "Contacts", label: c.name, sub: `${c.email ?? ""} · ${c.accountName}`, to: `/accounts/${c.accountId}` }));
  res?.tickets.forEach((t) => items.push({ key: "t" + t.id, group: "Tickets", label: `T-${t.number} ${t.title}`, sub: t.accountName, to: `/tickets/${t.id}` }));
  res?.emails.forEach((e) => items.push({ key: "e" + e.id, group: "Emails", label: e.subject || "(no subject)", sub: e.fromAddress, to: `/inbox/${e.threadId}` }));

  const go = (i: number) => { const it = items[i]; if (it) { nav(it.to); onClose(); } };
  let lastGroup = "";
  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk" role="dialog" aria-label="Search">
        <input
          ref={inputRef}
          value={q}
          placeholder="Search accounts, contacts, tickets, emails"
          onChange={(e) => { setQ(e.target.value); setActive(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter") go(active);
            if (e.key === "Escape") onClose();
          }}
        />
        <div className="results">
          {q.trim().length >= 2 && items.length === 0 && <div className="empty">No matches</div>}
          {items.map((it, i) => {
            const header = it.group !== lastGroup ? <div className="group">{it.group}</div> : null;
            lastGroup = it.group;
            return (
              <div key={it.key}>
                {header}
                <div className={`item ${i === active ? "active" : ""}`} onMouseEnter={() => setActive(i)} onClick={() => go(i)}>
                  <span className="truncate">{it.label}</span>
                  {it.sub && <span className="muted small truncate ml-auto" style={{ maxWidth: "45%" }}>{it.sub}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
